import { useMemo, useState } from "react";

import { Button } from "@/components/ui/Button";
import { DateTimeField } from "@/components/ui/DateTimeField";
import { EmailInput } from "@/components/ui/EmailInput";
import { SelectField, TextAreaField, TextField } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { createMeeting, updateMeeting, type MeetingInput } from "@/lib/api/meetings";
import type { Meeting, Recurrence } from "@/lib/api/types";
import {
  addMinutes,
  dayNumberOf,
  detectedZone,
  instantOf,
  isKnownZone,
  knownZones,
  offsetLabel,
  wallClockIn,
  weekdayOf,
  zoneCity,
  type WallClock,
} from "@/lib/calendar";
import { useI18n } from "@/lib/i18n";

function defaultStart(zone: string): WallClock {
  const now = wallClockIn(new Date(), zone);

  return addMinutes(now, 30 - (now.minute % 30), zone);
}

const REPEATS = [
  { id: "none", kind: "none", interval: 1, key: "schedule.repeatNone" },
  { id: "daily", kind: "daily", interval: 1, key: "schedule.repeatDaily" },
  { id: "weekly", kind: "weekly", interval: 1, key: "schedule.repeatWeekly" },
  { id: "weekly2", kind: "weekly", interval: 2, key: "schedule.repeatTwoWeeks" },
  { id: "weekly3", kind: "weekly", interval: 3, key: "schedule.repeatThreeWeeks" },
  { id: "monthly", kind: "monthly", interval: 1, key: "schedule.repeatMonthly" },
  { id: "monthly2", kind: "monthly", interval: 2, key: "schedule.repeatTwoMonths" },
  { id: "monthly3", kind: "monthly", interval: 3, key: "schedule.repeatThreeMonths" },
] as const;

type RepeatId = (typeof REPEATS)[number]["id"];

export type ScheduleDialogProps = {
  meeting?: Meeting | undefined;
  onClose: () => void;
  onSaved: (meeting: Meeting) => void;
};

export function ScheduleDialog({ meeting, onClose, onSaved }: ScheduleDialogProps) {
  const toast = useToast();
  const { t, locale } = useI18n();
  const editing = meeting !== undefined;

  const firstZone =
    meeting !== undefined && isKnownZone(meeting.timezone) ? meeting.timezone : detectedZone();

  const [zone, setZone] = useState(firstZone);
  const [title, setTitle] = useState(meeting?.title ?? "");
  const [description, setDescription] = useState(meeting?.description ?? "");
  const [startsAt, setStartsAt] = useState<WallClock>(() =>
    meeting === undefined
      ? defaultStart(firstZone)
      : wallClockIn(new Date(meeting.startsAt), firstZone),
  );
  const [endsAt, setEndsAt] = useState<WallClock>(() =>
    meeting === undefined
      ? addMinutes(defaultStart(firstZone), 60, firstZone)
      : wallClockIn(new Date(meeting.endsAt), firstZone),
  );
  const [repeat, setRepeat] = useState<RepeatId>(() => repeatIdOf(meeting?.recurrence));
  const [invitees, setInvitees] = useState<string[]>(
    meeting?.invitees.map((invitee) => invitee.email) ?? [],
  );
  const [busy, setBusy] = useState(false);

  const zones = useMemo(() => {
    const now = new Date();
    return knownZones()
      .map((id) => ({
        value: id,
        label: `${zoneCity(id)} (${offsetLabel(id, now)})`,
        city: zoneCity(id),
      }))
      .toSorted((a, b) => a.city.localeCompare(b.city, locale))
      .map(({ value, label }) => ({ value, label }));
  }, [locale]);

  const start = instantOf(startsAt, zone);
  const end = instantOf(endsAt, zone);
  const ordered = end.getTime() > start.getTime();
  const valid = title.trim() !== "" && ordered;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const input: MeetingInput = {
        title: title.trim(),
        description: description.trim(),
        startsAt: start.toISOString(),
        endsAt: end.toISOString(),
        timezone: zone,
        recurrence: buildRecurrence(repeat, startsAt),
        invitees: invitees.map((email) => ({ email })),
      };
      const saved = editing ? await updateMeeting(meeting.id, input) : await createMeeting(input);
      onSaved(saved);
      toast.show(editing ? t("schedule.saved") : t("schedule.created"), "success");
      onClose();
    } catch (error) {
      toast.fail(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button disabled={!valid} form="schedule-form" loading={busy} type="submit">
            {editing ? t("common.save") : t("schedule.submit")}
          </Button>
        </>
      }
      onClose={onClose}
      size="lg"
      title={editing ? t("schedule.editTitle") : t("schedule.title")}
    >
      <form
        className="flex flex-col gap-4"
        id="schedule-form"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <TextField
          label={t("schedule.titleField")}
          maxLength={120}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
          placeholder={t("schedule.titlePlaceholder")}
          required
          value={title}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <DateTimeField
            label={t("schedule.starts")}
            onChange={(next) => {
              setStartsAt(next);

              if (instantOf(endsAt, zone).getTime() <= instantOf(next, zone).getTime()) {
                setEndsAt(addMinutes(next, 60, zone));
              }
            }}
            timeZone={zone}
            value={startsAt}
          />
          <DateTimeField
            error={ordered ? undefined : t("schedule.endsBeforeStart")}
            label={t("schedule.ends")}
            onChange={setEndsAt}
            timeZone={zone}
            value={endsAt}
          />
        </div>

        <SelectField
          hint={t("schedule.timezoneHint")}
          label={t("schedule.timezone")}
          onChange={setZone}
          options={zones}
          value={zone}
        />

        <SelectField
          label={t("schedule.repeat")}
          onChange={(value) => {
            setRepeat(toRepeatId(value));
          }}
          options={REPEATS.map((entry) => ({ value: entry.id, label: t(entry.key) }))}
          value={repeat}
        />

        <EmailInput
          hint={t("schedule.inviteHint")}
          label={t("schedule.inviteByEmail")}
          onChange={setInvitees}
          placeholder={t("schedule.emailsPlaceholder")}
          value={invitees}
        />

        <TextAreaField
          label={t("schedule.description")}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          placeholder={t("schedule.descriptionPlaceholder")}
          value={description}
        />
      </form>
    </Modal>
  );
}

function toRepeatId(value: string): RepeatId {
  return REPEATS.find((entry) => entry.id === value)?.id ?? "none";
}

function repeatIdOf(recurrence: Recurrence | undefined): RepeatId {
  if (recurrence === undefined || recurrence.kind === "none") return "none";
  const interval = recurrence.interval ?? 1;
  const exact = REPEATS.find(
    (entry) => entry.kind === recurrence.kind && entry.interval === interval,
  );
  return exact?.id ?? recurrence.kind;
}

function buildRecurrence(id: RepeatId, startsAt: WallClock): Recurrence {
  const entry = REPEATS.find((candidate) => candidate.id === id);

  const every = entry === undefined || entry.interval === 1 ? {} : { interval: entry.interval };
  switch (entry?.kind) {
    case "daily":
      return { kind: "daily", until: null, ...every };
    case "weekly":
      return {
        kind: "weekly",
        days: [weekdayOf(dayNumberOf(startsAt.year, startsAt.month, startsAt.day))],
        until: null,
        ...every,
      };
    case "monthly":
      return { kind: "monthly", day: startsAt.day, until: null, ...every };
    case "none":
    case undefined:
      return { kind: "none" };
  }
}
